// Hex Grid Visualization for Three.js
// IMPORTANT: gameplay/placement logic still comes from browserMarsColony's
// mature hexgrid + em2c-store systems. This file only controls how that grid
// is rendered over the newer MarsColonyCGPT terrain.

import React, { useMemo, useRef, useEffect } from "react";
import * as THREE from "three";
import { useGame } from "../em2c-store";
import { HEX_SIZE, axialToWorld } from "../hexgrid";
import {
  getActiveCommandCoverageHexes,
  getConnectedCommandNodes,
  getHexesInCommandNodeCoverage,
  isCommandNodeType,
} from "../systems/CommandNetworkIntegration";
import { marsHeight } from "./CGPTWorldTerrain";

const keyOf = (q, r) => `${q},${r}`;

// Flat-top hex geometry retained for deposit fills / placement state overlays.
export function createHexGeometry(size) {
  const shape = new THREE.Shape();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i);
    const x = size * Math.cos(angle);
    const y = size * Math.sin(angle);
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  const geom = new THREE.ShapeGeometry(shape);
  geom.rotateX(-Math.PI / 2);
  return geom;
}

function hexCenterY(x, z, offset = 0.035) {
  return marsHeight(x, z) + offset;
}

function getHexCornerXZ(cx, cz, i, radius) {
  const angle = (Math.PI / 180) * (60 * i);
  return { x: cx + radius * Math.cos(angle), z: cz + radius * Math.sin(angle) };
}

function CoverageHexLayer({ hexes, color, opacity = 0.16, boundaryOpacity = 0.9, yOffset = 0.07 }) {
  const meshRef = useRef();
  const hexGeom = useMemo(() => createHexGeometry(HEX_SIZE * 0.87), []);

  useEffect(() => {
    if (!meshRef.current || !hexes.length) return;
    const dummy = new THREE.Object3D();
    hexes.forEach((hex, i) => {
      dummy.position.set(hex.x, hexCenterY(hex.x, hex.z, yOffset), hex.z);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    });
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [hexes, yOffset]);

  const boundaryGeometry = useMemo(() => {
    if (!hexes.length) return null;
    const covered = new Set(hexes.map((hex) => keyOf(hex.q, hex.r)));
    const positions = [];
    const radius = HEX_SIZE * 0.91;

    for (const hex of hexes) {
      const neighborDirs = [
        { q: 1, r: 0 }, { q: 1, r: -1 }, { q: 0, r: -1 },
        { q: -1, r: 0 }, { q: -1, r: 1 }, { q: 0, r: 1 },
      ];
      for (let i = 0; i < 6; i++) {
        const n = neighborDirs[i];
        if (covered.has(keyOf(hex.q + n.q, hex.r + n.r))) continue;
        const c1 = getHexCornerXZ(hex.x, hex.z, i, radius);
        const c2 = getHexCornerXZ(hex.x, hex.z, i + 1, radius);
        positions.push(
          c1.x, hexCenterY(c1.x, c1.z, yOffset + 0.018), c1.z,
          c2.x, hexCenterY(c2.x, c2.z, yOffset + 0.018), c2.z,
        );
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return geometry;
  }, [hexes, yOffset]);

  if (!hexes.length) return null;

  return (
    <group>
      <instancedMesh ref={meshRef} args={[hexGeom, undefined, hexes.length]} frustumCulled={false}>
        <meshBasicMaterial color={color} transparent opacity={opacity} side={THREE.DoubleSide} depthWrite={false} />
      </instancedMesh>
      {boundaryGeometry && (
        <lineSegments geometry={boundaryGeometry}>
          <lineBasicMaterial color={color} transparent opacity={boundaryOpacity} depthWrite={false} />
        </lineSegments>
      )}
    </group>
  );
}

// Render subtle state fills. During build/move mode this also becomes the clear
// command-network map: active drone territory is cyan, territory outside the
// network is faint red, and a hovered Command/Relay node previews its new reach.
export function HexGridOverlay({ grid, visible, hoverHex, validHexes, placementFootprint }) {
  const meshRef = useRef();
  const buildings = useGame((s) => s.buildings);
  const selectedId = useGame((s) => s.selectedId);
  const placementType = useGame((s) => s.placementType);
  const placementRotation = useGame((s) => s.placementRotation);
  const moveBuildingId = useGame((s) => s.moveBuildingId);
  const moveCandidate = useGame((s) => s.moveCandidate);
  const storeHoverHex = useGame((s) => s.hoverHex);
  const isValidHexPlacement = useGame((s) => s.isValidHexPlacement);
  const isValidMovePlacement = useGame((s) => s.isValidMovePlacement);

  const hexes = useMemo(() => {
    if (!grid) return [];
    return Array.from(grid.values());
  }, [grid]);

  const selected = useMemo(
    () => buildings.find((building) => building.id === selectedId) || null,
    [buildings, selectedId],
  );
  const movingBuilding = useMemo(
    () => buildings.find((building) => building.id === moveBuildingId) || null,
    [buildings, moveBuildingId],
  );

  const networkPlacementMode = Boolean(placementType || moveBuildingId);
  const selectedNodeMode = !networkPlacementMode && isCommandNodeType(selected?.type);

  const coverageHexes = useMemo(() => {
    if (!grid) return [];
    if (networkPlacementMode) {
      return getActiveCommandCoverageHexes(
        { buildings, grid },
        grid,
        moveBuildingId || null,
      );
    }
    if (selectedNodeMode && selected) return getHexesInCommandNodeCoverage(grid, selected);
    return [];
  }, [grid, buildings, moveBuildingId, networkPlacementMode, selectedNodeMode, selected]);

  const coverageKeys = useMemo(
    () => new Set(coverageHexes.map((hex) => keyOf(hex.q, hex.r))),
    [coverageHexes],
  );

  const connectedNodes = useMemo(
    () => getConnectedCommandNodes({ buildings }),
    [buildings],
  );
  const selectedNodeConnected = selectedNodeMode && selected
    ? connectedNodes.has(selected.id)
    : true;

  const previewTarget = moveCandidate || storeHoverHex;
  const previewType = placementType || movingBuilding?.type || null;
  const previewNodeMode = networkPlacementMode && previewTarget && isCommandNodeType(previewType);

  const previewValidity = useMemo(() => {
    if (!previewNodeMode || !previewTarget) return null;
    if (moveBuildingId && typeof isValidMovePlacement === "function") {
      return isValidMovePlacement(previewTarget.q, previewTarget.r);
    }
    if (placementType && typeof isValidHexPlacement === "function") {
      return isValidHexPlacement(placementType, previewTarget.q, previewTarget.r, placementRotation || 0);
    }
    return null;
  }, [
    previewNodeMode,
    previewTarget,
    moveBuildingId,
    placementType,
    placementRotation,
    isValidMovePlacement,
    isValidHexPlacement,
  ]);

  const previewHexes = useMemo(() => {
    if (!grid || !previewNodeMode || !previewTarget || !previewType) return [];
    return getHexesInCommandNodeCoverage(grid, {
      type: previewType,
      q: previewTarget.q,
      r: previewTarget.r,
    });
  }, [grid, previewNodeMode, previewTarget, previewType]);

  useEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    const color = new THREE.Color();

    hexes.forEach((hex, i) => {
      dummy.position.set(hex.x, hexCenterY(hex.x, hex.z, 0.025), hex.z);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);

      const key = keyOf(hex.q, hex.r);
      if (networkPlacementMode && !coverageKeys.has(key)) color.set("#7a2424");
      else if (validHexes?.has(key)) color.set("#00fa9a");
      else if (placementFootprint?.has(key)) color.set("#ff3366");
      else if ((hoverHex || storeHoverHex) && (hoverHex || storeHoverHex).q === hex.q && (hoverHex || storeHoverHex).r === hex.r) color.set("#43d8f4");
      else if (!hex.buildable) color.set("#65302d");
      else if (hex.occupied) color.set("#303941");
      else color.set("#43d8f4");

      meshRef.current.setColorAt(i, color);
    });

    meshRef.current.instanceMatrix.needsUpdate = true;
    if (meshRef.current.instanceColor) meshRef.current.instanceColor.needsUpdate = true;
  }, [
    hexes,
    hoverHex,
    storeHoverHex,
    validHexes,
    placementFootprint,
    networkPlacementMode,
    coverageKeys,
  ]);

  const hexGeom = useMemo(() => createHexGeometry(HEX_SIZE * 0.90), []);
  if (!visible || hexes.length === 0) return null;

  const coverageColor = selectedNodeMode && !selectedNodeConnected ? "#ff8c00" : "#43d8f4";
  const previewColor = previewValidity?.valid ? "#00fa9a" : "#ff3366";

  return (
    <group>
      <instancedMesh ref={meshRef} args={[hexGeom, undefined, hexes.length]} visible={visible}>
        <meshBasicMaterial
          transparent
          opacity={networkPlacementMode ? 0.075 : 0.055}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </instancedMesh>

      {(networkPlacementMode || selectedNodeMode) && (
        <CoverageHexLayer
          hexes={coverageHexes}
          color={coverageColor}
          opacity={networkPlacementMode ? 0.16 : 0.19}
          boundaryOpacity={0.95}
          yOffset={0.07}
        />
      )}

      {previewNodeMode && previewHexes.length > 0 && (
        <CoverageHexLayer
          hexes={previewHexes}
          color={previewColor}
          opacity={previewValidity?.valid ? 0.22 : 0.18}
          boundaryOpacity={1}
          yOffset={0.09}
        />
      )}
    </group>
  );
}

// Thin cyan hex wireframe, terrain-following. Placement math is unchanged.
export function HexGridLines({ grid, visible }) {
  const geometry = useMemo(() => {
    if (!grid || !visible) return null;
    const positions = [];
    const hexes = Array.from(grid.values());
    const radius = HEX_SIZE * 0.90;

    for (const hex of hexes) {
      for (let i = 0; i < 6; i++) {
        const angle1 = (Math.PI / 180) * (60 * i);
        const angle2 = (Math.PI / 180) * (60 * (i + 1));
        const x1 = hex.x + radius * Math.cos(angle1);
        const z1 = hex.z + radius * Math.sin(angle1);
        const x2 = hex.x + radius * Math.cos(angle2);
        const z2 = hex.z + radius * Math.sin(angle2);
        positions.push(
          x1, hexCenterY(x1, z1, 0.045), z1,
          x2, hexCenterY(x2, z2, 0.045), z2
        );
      }
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return geom;
  }, [grid, visible]);

  if (!geometry || !visible) return null;

  return (
    <lineSegments geometry={geometry}>
      <lineBasicMaterial color="#43d8f4" transparent opacity={0.20} depthWrite={false} />
    </lineSegments>
  );
}

// Placement footprint keeps browserMarsColony's validation result, but uses the
// cleaner CGPT visual treatment and follows the new terrain elevation.
export function HexPlacementGhost({ originQ, originR, footprint, valid, placement }) {
  const color = useMemo(() => {
    switch (placement) {
      case "valid": return "#00fa9a";
      case "blocked": return "#ff3366";
      case "warning": return "#ffc107";
      case "connector": return "#43d8f4";
      case "terrain": return "#e8a37e";
      default: return valid ? "#00fa9a" : "#ff3366";
    }
  }, [placement, valid]);

  const hexes = useMemo(() => {
    if (!footprint) return [];
    return footprint.map(({ q, r }) => {
      const wq = originQ + q;
      const wr = originR + r;
      const pos = axialToWorld(wq, wr);
      return { ...pos, q: wq, r: wr, y: hexCenterY(pos.x, pos.z, 0.06) };
    });
  }, [originQ, originR, footprint]);

  const hexGeom = useMemo(() => createHexGeometry(HEX_SIZE * 0.88), []);

  return (
    <group>
      {hexes.map((hex, i) => (
        <mesh key={i} position={[hex.x, hex.y, hex.z]} geometry={hexGeom}>
          <meshBasicMaterial color={color} transparent opacity={0.22} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
      ))}
      {hexes.map((hex, i) => (
        <lineSegments key={`line-${i}`} position={[hex.x, hex.y + 0.01, hex.z]}>
          <edgesGeometry args={[createHexGeometry(HEX_SIZE * 0.88)]} />
          <lineBasicMaterial color={color} transparent opacity={0.82} depthWrite={false} />
        </lineSegments>
      ))}
    </group>
  );
}

// Connector rules/edge selection remain entirely from the existing game logic.
export function HexConnectors({ building, grid }) {
  if (!building || !grid) return null;

  const center = axialToWorld(building.q, building.r);
  const connectors = [];

  const addPorts = (ports, color, size) => {
    if (!ports) return;
    for (const edge of ports) {
      const angle = (Math.PI / 180) * (60 * edge + building.rotation);
      const x = center.x + HEX_SIZE * 0.5 * Math.cos(angle);
      const z = center.z + HEX_SIZE * 0.5 * Math.sin(angle);
      connectors.push({ x, z, y: hexCenterY(x, z, 0.3), color, size });
    }
  };

  addPorts(building.powerPorts, "#ffc107", 0.15);
  addPorts(building.waterPorts, "#43d8f4", 0.15);
  addPorts(building.oxygenPorts, "#7fffd4", 0.15);
  addPorts(building.corridorPorts, "#e2e6ee", 0.20);

  return (
    <group>
      {connectors.map((c, i) => (
        <mesh key={i} position={[c.x, c.y, c.z]}>
          <sphereGeometry args={[c.size, 8, 8]} />
          <meshBasicMaterial color={c.color} transparent opacity={0.8} />
        </mesh>
      ))}
    </group>
  );
}
